/**
 * Utility functions for handling Sanity stega encoding in draft mode
 */

/**
 * Cleans stega-encoded strings by removing invisible Unicode characters
 * that are added by Sanity's stega encoding for visual editing
 *
 * @param value - The potentially stega-encoded string
 * @returns The cleaned string with invisible characters removed
 */
export function cleanStegaString(value: string | undefined | null): string {
  if (!value) return '';

  return value
    .replace(/[\u200B-\u200D\uFEFF\u2060]/g, '') // Remove zero-width characters
    .trim();
}

/**
 * Recursively cleans all string values in an object/array structure
 * This is more efficient than maintaining a list of specific keys
 *
 * @param data - The data structure to clean
 * @returns The cleaned data structure
 */
export function cleanStegaDataRecursively(data: any): any {
  if (data === null || data === undefined) return data;

  if (typeof data === 'string') {
    return cleanStegaString(data);
  }

  if (Array.isArray(data)) {
    return data.map((item) => cleanStegaDataRecursively(item));
  }

  if (typeof data === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      cleaned[key] = cleanStegaDataRecursively(value);
    }
    return cleaned;
  }

  return data;
}

/**
 * Cleans an object's string properties that might contain stega encoding
 *
 * @param obj - The object to clean
 * @param stringKeys - Array of keys that should be cleaned as strings
 * @returns A new object with cleaned string properties
 */
export function cleanStegaObject<T extends Record<string, any>>(obj: T, stringKeys: (keyof T)[]): T {
  const cleaned = { ...obj };

  stringKeys.forEach((key) => {
    if (typeof cleaned[key] === 'string') {
      cleaned[key] = cleanStegaString(cleaned[key]) as T[keyof T];
    }
  });

  return cleaned;
}

/**
 * Cleans an array of objects that might contain stega-encoded strings
 *
 * @param array - Array of objects to clean
 * @param stringKeys - Array of keys that should be cleaned as strings
 * @returns A new array with cleaned objects
 */
export function cleanStegaArray<T extends Record<string, any>>(array: T[], stringKeys: (keyof T)[]): T[] {
  return array.map((item) => cleanStegaObject(item, stringKeys));
}
